import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Send, X } from "lucide-react";
import { api } from "../lib/api";
import { formatBytes } from "../lib/format";

/**
 * Two-step confirmation modal for the accountant delivery:
 *   Step 1 · summary of the batch (recipient, count, size, subject, message)
 *   Step 2 · L2 password confirmation
 *
 * The L2 stepup token is short-lived — we consume it in-flight and never
 * store it in local storage.
 */
export default function AccountantSendModal({
  open, onClose, docs, totalSize, fiscalYear,
  recipientEmail, recipientName, onSuccess,
}) {
  const [step, setStep] = useState(1);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);

  const close = () => {
    if (sending) return;
    setStep(1); setPassword(""); setSubject(""); setMessage("");
    onClose();
  };

  if (!open) return null;

  const doSend = async () => {
    if (!password) return toast.error("Inserisci la password per confermare");
    setSending(true);
    try {
      const stepup = await api.stepupPassword(password);
      const token = stepup?.stepup_token;
      if (!token) throw new Error("stepup missing");
      await api.accountantSend({
        document_ids: docs.map((d) => d.id),
        fiscal_year: fiscalYear || null,
        subject: subject || null,
        message: message || null,
      }, token);
      toast.success(`Inviati ${docs.length} documenti a ${recipientEmail}`);
      onSuccess?.();
      close();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        toast.error("Password non corretta");
      } else if (e?.response?.status === 502) {
        toast.error(detail || "Errore invio email. Riprova.");
      } else {
        toast.error(detail || "Errore invio");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="accountant-send-modal">
      <div className="pxos-card p-5 max-w-lg w-full space-y-4" role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-2">
          <h2 className="font-display text-lg font-semibold">
            {step === 1 ? "Conferma invio al commercialista" : "Verifica password"}
          </h2>
          <button onClick={close} className="p-1.5 rounded-full hover:bg-black/5" aria-label="Chiudi" data-testid="accountant-modal-close">
            <X size={16} />
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-black/5 p-3 space-y-1">
              <div><span className="opacity-60">Destinatario:</span> <strong data-testid="accountant-modal-recipient">{recipientName ? `${recipientName} — ` : ""}{recipientEmail}</strong></div>
              <div><span className="opacity-60">Documenti:</span> <strong data-testid="accountant-modal-count">{docs.length}</strong> · <span data-testid="accountant-modal-size">{formatBytes(totalSize)}</span></div>
              {fiscalYear && <div><span className="opacity-60">Anno fiscale:</span> <strong>{fiscalYear}</strong></div>}
            </div>

            <div>
              <label className="text-xs opacity-70">Oggetto (opzionale)</label>
              <input
                type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder={fiscalYear ? `Documenti fiscali ${fiscalYear}` : "Documenti fiscali"}
                className="w-full rounded-lg border px-3 py-2 text-sm min-h-[40px] mt-1"
                data-testid="accountant-modal-subject"
                maxLength={200}
              />
            </div>
            <div>
              <label className="text-xs opacity-70">Messaggio (opzionale)</label>
              <textarea
                value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Es. In allegato i documenti da registrare per il primo trimestre."
                className="w-full rounded-lg border px-3 py-2 text-sm mt-1 resize-none"
                rows={4}
                data-testid="accountant-modal-message"
                maxLength={4000}
              />
            </div>

            <div className="text-xs opacity-70 flex items-start gap-1.5">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              Una volta inviati, i documenti saranno marcati come "già trasmessi"
              e non compariranno più nell'inbox. L'operazione richiede la password.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={close} className="px-4 py-2 rounded-lg border text-sm min-h-[36px]" data-testid="accountant-modal-cancel">
                Annulla
              </button>
              <button
                onClick={() => setStep(2)}
                className="pxos-btn-primary text-sm px-4 py-2 min-h-[36px] flex items-center gap-1.5"
                data-testid="accountant-modal-next"
              >
                <Send size={14} /> Continua
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 text-sm">
            <div>Per procedere all'invio, conferma la tua password.</div>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-lg border px-3 py-2 text-sm min-h-[40px]"
              autoFocus
              data-testid="accountant-modal-password"
              onKeyDown={(e) => { if (e.key === "Enter") doSend(); }}
            />
            <div className="flex justify-between gap-2 pt-2">
              <button
                onClick={() => setStep(1)}
                disabled={sending}
                className="px-3 py-2 rounded-lg border text-sm min-h-[36px]"
                data-testid="accountant-modal-back"
              >
                Indietro
              </button>
              <button
                onClick={doSend}
                disabled={sending || !password}
                className="pxos-btn-primary text-sm px-4 py-2 min-h-[36px] flex items-center gap-1.5 disabled:opacity-50"
                data-testid="accountant-modal-confirm"
              >
                {sending ? <><Loader2 size={14} className="animate-spin" /> Invio…</> : <><Send size={14} /> Invia adesso</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
